import apiClient from './client';

export interface Employee {
  can_suggest_tasks?: number | boolean;
  id: string;
  user_id?: number;
  name: string;
  email: string;
  phone: string;
  role: string;
  crew?: string;
  status: 'active' | 'inactive';
  hire_date: string;
  pay_rate?: number;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  profile_image?: string;
  employee_type?: 'employee' | 'subcontractor';
  company_name?: string;
  wcb_clearance_number?: string;
  wcb_clearance_expiry?: string;
  liability_insurance_expiry?: string;
  sin_last4?: string;
}

export interface EmployeeScheduleEntry {
  id: number;
  job_id: number;
  job_title?: string;
  site_address?: string;
  crew_name?: string;
  scheduled_date: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  status: string;
  notes?: string;
}

export interface EmployeePaystub {
  id: number;
  pay_date: string;
  pay_period_start: string;
  pay_period_end: string;
  status: string;
  regular_hours: number;
  ot_1_5_hours: number;
  ot_2_0_hours: number;
  hourly_rate: number;
  regular_pay: number;
  ot_1_5_pay: number;
  ot_2_0_pay: number;
  gross_pay: number;
  cpp: number;
  ei: number;
  federal_tax: number;
  provincial_tax: number;
  total_deductions: number;
  net_pay: number;
  ytd_gross: number;
}

export interface EmployeeDocument {
  id: number;
  job_id: number;
  job_title?: string;
  file_name: string;
  document_type?: string;
  file_type?: string;
  created_at: string;

  // Additional optional fields used by UI
  document_category?: string;
  file_path?: string;
  file_size?: number;
  uploaded_at?: string;
  caption?: string;
}

export interface EmployeeTimeOff {
  id: number;
  employee_id: number;
  type: 'vacation' | 'sick' | 'personal' | 'unpaid' | 'other';
  start_date: string;
  end_date: string;
  status: 'pending' | 'approved' | 'denied';
  notes?: string;
  created_by?: number;
  created_at?: string;
}

export interface EmployeeSkill {
  id: number;
  employee_id: number;
  skill_name: string;
  category: 'certification' | 'ticket' | 'license' | 'skill' | 'other';
  issue_date?: string;
  expiry_date?: string;
  notes?: string;
}

export interface EmployeeStats {
  total: number;
  active: number;
  inactive: number;
}

export const getEmployees = async (search?: string): Promise<Employee[]> => {
  const response = await apiClient.get('/employees', { 
    params: search ? { search } : undefined 
  });
  return response.data.data || response.data || [];
};

export const getEmployeeById = async (id: string): Promise<Employee> => {
  const response = await apiClient.get(`/employees/${id}`);
  return response.data.data || response.data;
};

export const getEmployeeStats = async (): Promise<EmployeeStats> => {
  const response = await apiClient.get('/employees/stats');
  return response.data.data || response.data;
};

export const createEmployee = async (data: Partial<Employee>): Promise<Employee> => {
  const response = await apiClient.post('/employees', data);
  return response.data.data || response.data;
};

export const updateEmployee = async (id: string, data: Partial<Employee>): Promise<Employee> => {
  const response = await apiClient.put(`/employees/${id}`, data);
  return response.data.data || response.data;
};

export const deleteEmployee = async (id: string): Promise<void> => {
  await apiClient.delete(`/employees/${id}`);
};

export const getEmployeeSchedule = async (id: string): Promise<EmployeeScheduleEntry[]> => {
  const response = await apiClient.get(`/employees/${id}/schedule`);
  return response.data.data || [];
};

export const getEmployeePaystubs = async (id: string): Promise<EmployeePaystub[]> => {
  const response = await apiClient.get(`/employees/${id}/paystubs`);
  return response.data.data || [];
};

export const getEmployeeDocuments = async (id: string): Promise<EmployeeDocument[]> => {
  const response = await apiClient.get(`/employees/${id}/documents`);
  return response.data.data || [];
};

export const uploadEmployeeDocument = async (employeeId: string, file: File, category?: string): Promise<any> => {
  const form = new FormData();
  form.append('file', file as any);
  if (category) form.append('category', category);
  const response = await apiClient.post(`/employees/${employeeId}/documents`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data.data;
};

export const deleteEmployeeDocument = async (employeeId: string, documentId: string | number): Promise<void> => {
  await apiClient.delete(`/employees/${employeeId}/documents/${documentId}`);
};

export const getEmployeeTimeOff = async (id: string): Promise<EmployeeTimeOff[]> => {
  const response = await apiClient.get(`/employees/${id}/timeoff`);
  return response.data.data || [];
};

export const createEmployeeTimeOff = async (id: string, data: Partial<EmployeeTimeOff>): Promise<EmployeeTimeOff> => {
  const response = await apiClient.post(`/employees/${id}/timeoff`, data);
  return response.data.data;
};

export const updateEmployeeTimeOff = async (id: string, toid: number, data: Partial<EmployeeTimeOff>): Promise<EmployeeTimeOff> => {
  const response = await apiClient.patch(`/employees/${id}/timeoff/${toid}`, data);
  return response.data.data;
};

export const deleteEmployeeTimeOff = async (id: string, toid: number): Promise<void> => {
  await apiClient.delete(`/employees/${id}/timeoff/${toid}`);
};

export const getEmployeeSkills = async (id: string): Promise<EmployeeSkill[]> => {
  const response = await apiClient.get(`/employees/${id}/skills`);
  return response.data.data || [];
};

export const createEmployeeSkill = async (id: string, data: Partial<EmployeeSkill>): Promise<EmployeeSkill> => {
  const response = await apiClient.post(`/employees/${id}/skills`, data);
  return response.data.data;
};

export const updateEmployeeSkill = async (id: string, skillId: number, data: Partial<EmployeeSkill>): Promise<EmployeeSkill> => {
  const response = await apiClient.patch(`/employees/${id}/skills/${skillId}`, data);
  return response.data.data;
};

export const deleteEmployeeSkill = async (id: string, skillId: number): Promise<void> => {
  await apiClient.delete(`/employees/${id}/skills/${skillId}`);
};

// ── Job attribution (drives the worker's portal schedule + documents) ───────
export interface EmployeeJobAssignment {
  id: number;
  job_id: number;
  role?: string;
  assigned_at: string;
  job_title: string;
  job_status: string;
  job_number?: string;
}

export const getEmployeeJobAssignments = async (employeeId: string | number): Promise<EmployeeJobAssignment[]> => {
  const { data } = await apiClient.get(`/employees/${employeeId}/job-assignments`);
  return data.data;
};

export const assignEmployeeToJob = async (employeeId: string | number, jobId: number, role?: string): Promise<void> => {
  await apiClient.post(`/employees/${employeeId}/job-assignments`, { job_id: jobId, role });
};

export const unassignEmployeeFromJob = async (employeeId: string | number, jobId: number): Promise<void> => {
  await apiClient.delete(`/employees/${employeeId}/job-assignments/${jobId}`);
};

// ── Pay: payouts logged vs hours owed ───────────────────────────────────────
export interface PaySummary {
  hourly_rate: number;
  approved_hours: number;
  pending_hours: number;
  earned: number;
  paid: number;
  owed: number;
}

export interface EmployeePayout {
  id: number;
  amount: number;
  paid_on: string;
  method: string;
  period_start?: string | null;
  period_end?: string | null;
  notes?: string | null;
  created_by_name?: string;
}

export const getEmployeePay = async (employeeId: string | number): Promise<{ summary: PaySummary; payouts: EmployeePayout[] }> => {
  const { data } = await apiClient.get(`/employees/${employeeId}/pay`);
  return data.data;
};

export const addEmployeePayout = async (employeeId: string | number, payout: {
  amount: number; paid_on: string; method?: string;
  period_start?: string; period_end?: string; notes?: string;
}): Promise<void> => {
  await apiClient.post(`/employees/${employeeId}/pay`, payout);
};

export const deleteEmployeePayout = async (employeeId: string | number, payoutId: number): Promise<void> => {
  await apiClient.delete(`/employees/${employeeId}/pay/${payoutId}`);
};
